/**
 * @module AnimatedServiceCard
 * @description An animated card component for displaying service information with hover effects and animations.
 * 
 * @typedef {import('@/types').Service} Service
 * @typedef {import('@/types').AnimationConfig} AnimationConfig
 * 
 * @example
 * ```jsx
 * const service = {
 *   icon: 'fa-tree',
 *   title: 'Tree Service',
 *   description: 'Professional tree care and removal',
 *   benefits: ['Permanent installation', 'Site assessment']
 * };
 * 
 * <AnimatedServiceCard service={service} index={0} />
 * ```
 */

import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import { NeonIcon } from '@components/ui/NeonIcon';

/**
 * @type {AnimationConfig}
 */
const cardAnimation = {
  duration: 0.5,
  ease: [0.87, 0, 0.13, 1]
};

/**
 * Animated service card component
 * 
 * @param {Object} props - Component props
 * @param {Service} props.service - Service information to display
 * @param {number} props.index - Index for staggered animations
 * @returns {JSX.Element} Animated service card
 */
export function AnimatedServiceCard({ service, index }) {
  if (!service) {
    console.error('Service prop is required for AnimatedServiceCard');
    return (
      <div className="p-6 sm:p-8 rounded-2xl bg-red-50 text-red-600">
        <p>Error: Failed to load service information</p>
      </div>
    );
  }

  const cardRef = useRef(null);
  const iconRef = useRef(null);
  const contentRef = useRef(null);

  return (
    <motion.div
      className="glass-card overflow-hidden transition-all duration-300 hover:shadow-[0_0_30px_rgba(124,58,237,0.2)] hover:border-[#7C3AED]/30"
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      whileHover={{ y: -8 }}
      whileTap={{ scale: 0.98 }}
      transition={{ 
        duration: 0.5,
        delay: index * 0.1,
        ease: [0.87, 0, 0.13, 1]
      }}
    >
      <motion.div className="flex flex-col h-full">
        {/* Header */}
        <div className="bg-white/5 p-6 sm:p-8">
          <motion.div
            className="inline-block"
            whileHover={{
              scale: 1.1,
              rotate: 5,
              transition: { duration: 0.3, ease: [0.87, 0, 0.13, 1] }
            }}
          >
            <NeonIcon 
              icon={`fas ${service.icon}`} 
              color="text-[#06B6D4]" 
              glowColor="#06B6D4" 
              className="mb-4"
            />
          </motion.div>
          <h3 className="text-xl sm:text-2xl lg:text-2xl font-bold mb-3 text-white">
            {service.title}
          </h3>
          <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
            {service.description}
          </p>
        </div>

        {/* Content */}
        <div className="p-6 sm:p-8 flex-grow">
          <h4 className="text-sm font-semibold uppercase tracking-wider text-[#7C3AED] mb-4">Key Benefits</h4>
          <ul className="space-y-3 mb-6">
            {service.benefits.map((benefit, i) => (
              <li key={i} className="flex items-start text-base">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#06B6D4]/10 flex items-center justify-center mt-0.5 mr-3">
                  <i className="fas fa-check text-sm text-[#06B6D4]"></i>
                </span>
                <span className="text-gray-300">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Button Container */}
        <div className="px-6 sm:px-8 pb-6 sm:pb-8 space-y-3">
          {/* Quote Button */}
          <Link
            to="/contact"
            className="flex items-center justify-center w-full px-6 py-3 text-base font-semibold bg-[#7C3AED] text-white hover:bg-[#6D28D9] rounded-xl shadow-sm transition-all duration-200 group"
          >
            <span>Get Free Quote</span>
            <i className="fas fa-arrow-right ml-2 group-hover:translate-x-1 transition-transform" 
               aria-hidden="true"
            />
          </Link>

          {/* Call Button */}
          <motion.a
            href="tel:+13863077505"
            className={`
              flex items-center justify-center
              w-full
              px-6 py-3
              text-base font-semibold
              text-[#7C3AED]
              bg-[#7C3AED]/5
              hover:bg-[#7C3AED]/10
              rounded-xl
              transition-all duration-200
              group
            `}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <i className="fas fa-phone mr-2 group-hover:rotate-12 transition-transform" 
               aria-hidden="true"
            />
            <span>Call for Quick Service</span>
            <span className="ml-1 text-sm opacity-75">(24/7)</span>
          </motion.a>
        </div>
      </motion.div>
    </motion.div>
  );
}

AnimatedServiceCard.propTypes = {
  service: PropTypes.shape({
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    benefits: PropTypes.arrayOf(PropTypes.string).isRequired
  }).isRequired,
  index: PropTypes.number.isRequired
}; 